import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, MessageCircle, Megaphone, Bot, TrendingUp, BarChart3 } from 'lucide-react';
import Navbar from '@/components/landing/Navbar';
import Footer from '@/components/landing/Footer';
import SEO from '@/components/SEO';
import { Button } from '@/components/ui/button';
import { DASHBOARD_SIGNIN_URL, DASHBOARD_SIGNUP_URL } from '@/config/links';
import ProductFAQAccordion from '@/components/products/ProductFAQAccordion';

const engageFaqs = [
  { question: 'Can I automate WhatsApp campaigns?', answer: 'Yes, you can automate transactional and promotional WhatsApp journeys based on order events.' },
  { question: 'Does Engage+ include chatbot flows?', answer: 'Yes, chatbot automations are supported for common support and conversion use cases.' },
  { question: 'Can growth teams run segmented campaigns?', answer: 'Yes, you can create audience segments and schedule targeted campaign broadcasts.' },
  { question: 'How does Engage+ improve conversion rate?', answer: 'It uses timely, personalized engagement to recover intent and drive repeat purchases.' },
  { question: 'Is this suitable for D2C and enterprise teams?', answer: 'Yes, the suite is designed for SMB and enterprise growth teams with scalable workflows.' }
];

const ProductEngagePlus = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Engage+ for WhatsApp Automation and Retention Marketing"
        description="Drive retention with WhatsApp automation, campaign orchestration, chatbot support, and conversion-focused engagement analytics."
        keywords="whatsapp marketing automation, ecommerce retention platform, campaign builder, chatbot engagement"
        canonical="https://boxdlogistics.in/products/engage-plus"
      />
      <Navbar />

      <section className="bg-gradient-to-b from-blue-100/55 via-blue-50/50 to-white px-4 pb-20 pt-28 md:pt-32">
        <div className="mx-auto max-w-7xl">
          <div className="grid gap-10 lg:grid-cols-2 lg:items-center">
            <div>
              <span className="inline-flex rounded-full border border-blue-200 bg-white px-4 py-1 text-sm font-semibold text-blue-900">
                Engage+ Growth Suite
              </span>
              <h1 className="mt-4 text-4xl font-bold leading-tight text-gray-900 md:text-6xl">
                Turn Conversations into Conversions with Engage+
              </h1>
              <p className="mt-5 max-w-xl text-lg text-gray-600">
                Build personalized customer journeys across WhatsApp campaigns, chatbots, and post-purchase automations from one growth dashboard.
              </p>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <Button className="bg-gradient-to-r from-blue-800 to-blue-900 text-white hover:from-blue-900 hover:to-blue-950" onClick={() => { window.location.href = DASHBOARD_SIGNUP_URL; }}>
                  Book Engage+ Demo <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                <Button variant="outline" className="border-blue-300 text-blue-900 hover:bg-blue-50" asChild>
                  <Link to="/contact">Talk to Growth Team</Link>
                </Button>
              </div>
            </div>

            <div className="rounded-3xl border border-blue-100 bg-white p-6 shadow-xl">
              <h2 className="text-lg font-semibold text-gray-900">Campaign Performance Console</h2>
              <div className="mt-4 rounded-2xl bg-gray-50 p-4">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-gray-700">WhatsApp Broadcast</p>
                  <span className="rounded-full bg-green-100 px-2 py-1 text-xs font-semibold text-green-700">Live</span>
                </div>
                <div className="mt-4 grid grid-cols-3 gap-3">
                  {[
                    ['Open Rate', '78%'],
                    ['CTR', '31%'],
                    ['Conversions', '12.4%']
                  ].map((item) => (
                    <div key={item[0]} className="rounded-xl bg-white p-3 text-center shadow-sm">
                      <p className="text-xs text-gray-500">{item[0]}</p>
                      <p className="mt-1 text-lg font-bold text-blue-900">{item[1]}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white px-4 py-16">
        <div className="mx-auto max-w-7xl space-y-14">
          {[
            {
              title: 'WhatsApp Automation Workflows',
              description: 'Trigger order confirmations, delivery nudges, and retention flows automatically based on real user events.',
              icon: MessageCircle,
              points: ['Cart recovery reminders', 'Post-delivery review prompts', 'NDR response automations'],
              reverse: false
            },
            {
              title: 'Campaign Builder for Marketing Teams',
              description: 'Design, schedule, and launch segmented campaigns with reusable templates and audience controls.',
              icon: Megaphone,
              points: ['Segment-wise broadcasts', 'A/B campaign variants', 'Offer and urgency controls'],
              reverse: true
            },
            {
              title: 'Chatbots that Keep Buyers Engaged',
              description: 'Resolve repetitive customer queries and guide users to conversion-friendly actions in real time.',
              icon: Bot,
              points: ['Order status quick replies', 'Policy and support automation', 'Smart upsell intent prompts'],
              reverse: false
            }
          ].map((section, index) => {
            const Icon = section.icon;
            const isAccent = index === 1;
            return (
              <div key={section.title} className={`grid gap-6 lg:grid-cols-2 ${section.reverse ? 'lg:[&>*:first-child]:order-2' : ''}`}>
                <div className="rounded-3xl border border-gray-200 bg-gradient-to-b from-white to-gray-50 p-7">
                  <span className={`inline-flex rounded-lg p-2 ${isAccent ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-900'}`}><Icon className="h-5 w-5" /></span>
                  <h2 className="mt-4 text-3xl font-bold text-gray-900">{section.title}</h2>
                  <p className="mt-3 text-gray-600">{section.description}</p>
                  <ul className="mt-4 space-y-2">
                    {section.points.map((point) => (
                      <li key={point} className="rounded-lg bg-white px-3 py-2 text-sm text-gray-700 shadow-sm">{point}</li>
                    ))}
                  </ul>
                </div>
                <div className="rounded-3xl border border-blue-100 bg-white p-7 shadow-sm">
                  <h3 className="text-lg font-semibold text-gray-900">Dashboard Mockup</h3>
                  <div className="mt-4 space-y-3">
                    <div className="rounded-xl bg-blue-50 p-4 text-sm text-blue-950">Campaign segment: High-value repeat buyers</div>
                    <div className="rounded-xl bg-gray-50 p-4 text-sm text-gray-700">Automation trigger: Delivered + 3 days</div>
                    <div className="rounded-xl bg-blue-50/70 p-4 text-sm text-blue-950">Bot flow completion: 82%</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <section className="bg-gradient-to-b from-blue-50/50 to-white px-4 py-16">
        <div className="mx-auto max-w-7xl">
          <h2 className="text-center text-3xl font-bold text-gray-900 md:text-4xl">Before vs After Using Engage+</h2>
          <div className="mt-8 grid gap-6 md:grid-cols-2">
            <div className="rounded-2xl border border-gray-200 bg-gray-50 p-6">
              <h3 className="text-xl font-semibold text-gray-800">Before Engage+</h3>
              <ul className="mt-4 space-y-2 text-sm text-gray-700">
                <li>Manual campaign execution across tools</li>
                <li>Low response rates and poor personalization</li>
                <li>Missed post-purchase retention opportunities</li>
              </ul>
            </div>
            <div className="rounded-2xl border border-blue-100 bg-blue-50/70 p-6">
              <h3 className="text-xl font-semibold text-blue-900">After Engage+</h3>
              <ul className="mt-4 space-y-2 text-sm text-blue-950">
                <li>Automated journeys triggered by live events</li>
                <li>Higher engagement with targeted messaging</li>
                <li>More repeat purchases from lifecycle campaigns</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white px-4 py-16">
        <div className="mx-auto grid max-w-6xl gap-5 md:grid-cols-3">
          {[
            { label: 'Engagement Lift', value: '+42%', icon: TrendingUp },
            { label: 'Repeat Conversion', value: '+28%', icon: BarChart3 },
            { label: 'Campaign CTR', value: '3.1x', icon: Megaphone }
          ].map((metric) => {
            const Icon = metric.icon;
            return (
              <div key={metric.label} className="rounded-2xl border border-blue-100 bg-gradient-to-br from-blue-50 to-white p-6 text-center">
                <Icon className="mx-auto h-6 w-6 text-blue-900" />
                <p className="mt-3 text-4xl font-bold text-blue-900">{metric.value}</p>
                <p className="mt-1 text-sm font-medium text-gray-600">{metric.label}</p>
              </div>
            );
          })}
        </div>
      </section>

      <section className="bg-gradient-to-r from-blue-800 to-blue-900 px-4 py-16 text-white">
        <div className="mx-auto max-w-5xl text-center">
          <h2 className="text-3xl font-bold md:text-4xl">Reduce RTO & Increase Conversions</h2>
          <p className="mx-auto mt-3 max-w-2xl text-blue-100">Use Engage+ to orchestrate communication journeys that convert once and retain forever.</p>
          <Button className="mt-7 bg-white text-blue-900 hover:bg-blue-50" onClick={() => { window.location.href = DASHBOARD_SIGNIN_URL; }}>
            Start with Engage+ <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </section>

      <ProductFAQAccordion title="Engage+ FAQs" items={engageFaqs} className="pt-16" />

      <Footer />
    </div>
  );
};

export default ProductEngagePlus;
